import { apiRequest } from "@talentpair/api";
import { CandidateChoicesT } from "@talentpair/types/entities/candidate";
import { JobChoicesT } from "@talentpair/types/entities/job";
import { ChoiceT } from "@talentpair/types/entities/misc";
import { OrgChoicesT } from "@talentpair/types/entities/org";
import { SearchChoicesT } from "@talentpair/types/search";
import {
  JobBoardChoicesT,
  JobBoardChoicesDataT,
  JobLocationDataT,
  JobLocationChoicesT,
} from "@talentpair/types/entities/public";
import lodash from "lodash";
import { displayLocation } from "../utils/entities/location";

export interface ChoiceTypesT {
  candidates: CandidateChoicesT;
  organizations: OrgChoicesT;
  jobs: JobChoicesT;
  search: SearchChoicesT;
  job_board: Omit<JobBoardChoicesT, "total">;
}
type ChoicesT = Partial<ChoiceTypesT>;
export type ChoiceEntityT = keyof ChoiceTypesT;

const choices: ChoicesT = {};

export function transformJobBoardChoices<
  InData extends JobLocationDataT,
  OutData extends JobLocationChoicesT,
>({ locations, ...data }: InData): OutData {
  // @ts-expect-error - eslint upgrade
  return {
    ...data,
    locations: locations.map((v) => ({ ...v, name: displayLocation(v, false) })),
  };
}

export function transformJobChoices({ status, ...other }: JobChoicesT): JobChoicesT {
  return {
    ...other,
    // capitalize all choices, rename "open" to "Opportunistic", and sort "Opportunistic" choice to end of list
    status:
      status
        ?.sort((a, b) => (a.name === "open" ? 1 : b.name === "open" ? -1 : 0))
        .map(({ id, name }) => ({
          id,
          name: name === "open" ? "Opportunistic" : lodash.upperFirst(name),
        })) || [],
  };
}

// @ts-expect-error - eslint upgrade
const fetch: <T>(entity: Exclude<ChoiceEntityT, "job_board">) => Promise<T> = async (
  entity: Exclude<ChoiceEntityT, "job_board">,
) => {
  if (choices[entity]) return choices[entity];
  if (entity === "jobs") {
    choices[entity] = await apiRequest
      .getMemoized<JobChoicesT>(`${entity}/choices/`)
      .then(({ data }) => transformJobChoices(data));
    return choices[entity];
  }
  const val = await apiRequest
    .getMemoized<CandidateChoicesT & OrgChoicesT & SearchChoicesT>(`${entity}/choices/`)
    .then(({ data }) => data);
  choices[entity] = val;
  return val;
};

const fetchJobBoardChoices: (org_slug?: string) => Promise<JobBoardChoicesT> = async (
  org_slug: string = location.hostname.split(".")[0],
) => {
  if (choices.job_board) return choices.job_board;
  if (!org_slug || ["jobs", "localhost"].includes(org_slug)) org_slug = "tp";
  const val = await apiRequest
    .getMemoized<JobBoardChoicesDataT>(`jobs/board/${org_slug}/choices/`)
    .then(({ data }) => transformJobBoardChoices<JobBoardChoicesDataT, JobBoardChoicesT>(data));
  choices.job_board = val;
  return val;
};

type ChoiceServiceT = {
  candidates(): Promise<CandidateChoicesT>;
  organizations(): Promise<OrgChoicesT>;
  jobs(): Promise<JobChoicesT>;
  search(): Promise<SearchChoicesT>;
  job_board(org_slug: string): Promise<JobBoardChoicesT>;
  getChoiceName(entity: ChoiceEntityT, key: string, id: number | null): string | null;
  // avoid using this unless in angular land!!!
  __choices: ChoicesT;
};

const ChoiceService: ChoiceServiceT = {
  candidates: () => fetch("candidates"),
  organizations: () => fetch("organizations"),
  jobs: () => fetch("jobs"),
  search: () => fetch("search"),
  job_board: fetchJobBoardChoices,

  getChoiceName: (entity: ChoiceEntityT, key: string, id: number | null) => {
    const choiceSet = lodash.get(choices, [entity, key]);
    const choice = choiceSet ? choiceSet.find((c: ChoiceT) => c.id === id) : null;
    return choice ? choice.name : null;
  },

  // Expose synchronous access for Angular only. React should always use async methods.
  __choices: choices,
};

export default ChoiceService;

export const _test = { choices, fetch, transformJobChoices };
